import { Button, Group } from '@mantine/core'
import { observer } from 'mobx-react-lite'

import { OrgTierMap } from '@showrunner/codex'

import { showUpgradeInquiryModal, useUpgrade } from '#components/Modals'
import { PaidFeatureNote } from '#components/Settings/Org/PaidFeatureNote'
import { Toast } from '#components/Toast'
import { IOrg, useMst } from '#state'

import { showCreateInviteModal } from './CreateInviteModal'

import styles from './CreateInviteButton.module.scss'

type InviteState =
  // Stripe workspace whose paid plan lapsed
  | 'lapsed'
  // Stripe workspace without a live subscription
  | 'no-subscription'
  // Free tier at its cap of 3 people, counting members and open invitations.
  | 'free-limit'
  // Plan seats are full and the user owns the workspace
  | 'seat-max'
  // Plan seats are full and the user is an admin, not the owner
  | 'seat-max-locked'
  // No gate applies
  | 'ready'

const getInviteState = ({
  lapsed,
  blocked,
  atFreeLimit,
  atSeatMax,
  isOwner,
}: {
  lapsed: boolean
  blocked: boolean
  atFreeLimit: boolean
  atSeatMax: boolean
  isOwner: boolean
}): InviteState => {
  if (lapsed) return 'lapsed'
  if (blocked) return 'no-subscription'
  if (atFreeLimit) return 'free-limit'
  if (atSeatMax) return isOwner ? 'seat-max' : 'seat-max-locked'
  return 'ready'
}

const NoSubscriptionToast = observer(function NoSubscriptionToast() {
  const { pending, startSelfServe } = useUpgrade()

  return (
    <Toast
      message={
        <PaidFeatureNote
          inline
          features={[{ name: 'inviting people' }]}
          action={
            <Button
              className={styles.toast_button___neutral}
              variant="outline"
              loading={pending}
              onClick={startSelfServe}
            >
              Upgrade
            </Button>
          }
        />
      }
      dismissable={false}
    />
  )
})

const FreeLimitToast = ({ workspace }: { workspace: IOrg }) => (
  <Toast
    message={
      <>
        Your workspace is on the&nbsp;<strong>Free Plan</strong>&nbsp;and is
        limited to 3 members.
      </>
    }
    dismissable={false}
  >
    <Button
      className={styles.toast_button___neutral}
      variant="outline"
      onClick={() => showUpgradeInquiryModal(workspace)}
    >
      Request upgrade
    </Button>
  </Toast>
)

const SeatMaxButton = ({ workspace }: { workspace: IOrg }) => {
  const { pending, upgrade } = useUpgrade()

  return (
    <Group justify="flex-end">
      <Button loading={pending} onClick={() => upgrade(workspace)}>
        Manage subscription
      </Button>
    </Group>
  )
}

const InviteButton = ({
  onClick,
  loading = false,
  disabled = false,
}: {
  onClick?: () => void
  loading?: boolean
  disabled?: boolean
}) => (
  <Group justify="flex-end">
    <Button onClick={onClick} loading={loading} disabled={disabled}>
      <i className="fa fa-plus is-link-icon"></i>
      Invite New Member
    </Button>
  </Group>
)

export const CreateInviteButton = observer(function CreateInviteButton({
  inviteCount,
  workspace,
  atSeatMax = false,
  seatsPending = false,
}: {
  inviteCount: number
  workspace: IOrg
  atSeatMax?: boolean
  seatsPending?: boolean
}) {
  const { user } = useMst()

  const atFreeLimit =
    workspace.tier === OrgTierMap.FREE &&
    workspace.members.length + inviteCount >= 3

  const inviteState = getInviteState({
    lapsed: workspace.isLapsedStripeAccount,
    blocked: workspace.hasNoBillingAccount,
    atFreeLimit,
    atSeatMax,
    isOwner: user.isCurrentOrgOwner,
  })

  const showModal = () => {
    if (workspace) {
      showCreateInviteModal({ org: workspace })
    }
  }

  switch (inviteState) {
    case 'lapsed':
      return <InviteButton disabled />
    case 'no-subscription':
      return <NoSubscriptionToast />
    case 'free-limit':
      return <FreeLimitToast workspace={workspace} />
    case 'seat-max':
      return <SeatMaxButton workspace={workspace} />
    case 'seat-max-locked':
      return <InviteButton disabled />
    case 'ready':
      return (
        <InviteButton
          onClick={showModal}
          loading={seatsPending}
          disabled={seatsPending}
        />
      )
  }
})
